import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const RoleLimitsTab = () => {
  const [roleLimits, setRoleLimits] = useState({
    admin: { dailyLimit: 200, enabled: true },
    operator: { dailyLimit: 50, enabled: true },
    auditor: { dailyLimit: 25, enabled: true }
  });

  const [hasChanges, setHasChanges] = useState(false);

  const roleDescriptions = {
    admin: "Administradores del sistema con acceso completo",
    operator: "Profesionales automotrices que realizan consultas diarias",
    auditor: "Personal de cumplimiento con acceso de solo lectura"
  };

  const handleLimitChange = (role, field, value) => {
    setRoleLimits(prev => ({
      ...prev,
      [role]: {
        ...prev?.[role],
        [field]: field === 'dailyLimit' ? parseInt(value) || 0 : value
      }
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Mock save operation
    console.log('Guardando límites de roles:', roleLimits);
    setHasChanges(false);
  };

  const handleReset = () => {
    setRoleLimits({
      admin: { dailyLimit: 200, enabled: true },
      operator: { dailyLimit: 50, enabled: true },
      auditor: { dailyLimit: 25, enabled: true }
    });
    setHasChanges(false);
  };

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
          <div>
            <h4 className="font-medium text-blue-900">Configuración de Límites por Rol</h4>
            <p className="text-sm text-blue-700 mt-1">
              Establezca los límites diarios de consultas VIN para cada rol de usuario. Los cambios se aplicarán inmediatamente a todos los usuarios del rol correspondiente.
            </p>
          </div>
        </div>
      </div>
      <div className="grid gap-6">
        {Object.entries(roleLimits)?.map(([role, config]) => (
          <div key={role} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  role === 'admin' ? 'bg-red-100 text-red-600' :
                  role === 'operator'? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'
                }`}>
                  <Icon 
                    name={role === 'admin' ? 'Shield' : role === 'operator' ? 'Users' : 'Eye'} 
                    size={20} 
                  />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground capitalize">
                    {role === 'admin' ? 'Administrador' : 
                     role === 'operator' ? 'Operador' : 'Auditor'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {roleDescriptions?.[role]}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <span className="text-sm text-muted-foreground">Activo</span>
                <button
                  onClick={() => handleLimitChange(role, 'enabled', !config?.enabled)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    config?.enabled ? 'bg-primary' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      config?.enabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                label="Límite Diario de Consultas"
                type="number"
                value={config?.dailyLimit}
                onChange={(e) => handleLimitChange(role, 'dailyLimit', e?.target?.value)}
                disabled={!config?.enabled}
                min="1"
                max="1000"
                description="Número máximo de consultas VIN por día"
              />
              
              <div className="flex flex-col justify-end">
                <div className="bg-muted rounded-lg p-3">
                  <div className="text-sm text-muted-foreground mb-1">Uso Actual Promedio</div>
                  <div className="text-lg font-semibold text-foreground">
                    {role === 'admin' ? '45' : role === 'operator' ? '32' : '12'} consultas/día
                  </div>
                  <div className="text-xs text-muted-foreground">
                    Basado en los últimos 30 días
                  </div>
                </div>
              </div>
            </div>

            {!config?.enabled && (
              <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Icon name="AlertTriangle" size={16} className="text-yellow-600" />
                  <span className="text-sm text-yellow-800">
                    Los usuarios con este rol no podrán realizar consultas mientras esté desactivado
                  </span>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {hasChanges && (
        <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <Icon name="AlertCircle" size={20} className="text-amber-600" />
            <span className="text-sm font-medium text-amber-800">
              Tienes cambios sin guardar
            </span>
          </div>
          <div className="flex space-x-3">
            <Button variant="outline" size="sm" onClick={handleReset}>
              Descartar
            </Button>
            <Button variant="default" size="sm" onClick={handleSave}>
              Guardar Cambios
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoleLimitsTab;